import "../../assets/css/clientAlerts/contractList.css"

import sortIcon from "../../assets/images/sort.svg"
import person from "../../assets/images/person.svg"
import reviewed from "../../assets/images/viewProfile.svg"

import { useEffect, useRef, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/auth-user"
import moment from "moment"
import { getContractsList, getContractsListFilters, reviewContract } from "./contractListUtils"
import PageSelector from "../pageSelector"

const ITEMS_PER_PAGE = 8

export default function ContractList() {
    const { user } = useAuth()
    const userLogged = user?.selectedProfile
    const darkLightMode = user?.selectedProfile?.darkLightMode
    const navigate = useNavigate()

    // Data paginada desde el backend
    const [data, setData] = useState([])
    const [total, setTotal] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)

    const [searchValue, setSearchValue] = useState("")
    const [searchValueApplied, setSearchValueApplied] = useState("")

    // Filtros y UI
    const [supplierFilter, setSupplierFilter] = useState([])
    const [supplierFilterApplied, setSupplierFilterApplied] = useState([])
    const [openFilter, setOpenFilter] = useState(null)
    const filterRef = useRef(null)

    // Ordenamiento
    const [order, setOrder] = useState("ASC")
    const [orderBy, setOrderBy] = useState("companyName")

    // Datos para construir los filtros
    const [filtersData, setFiltersData] = useState({
        suppliers: []
    })

    // Close menus filters on click outside
    useEffect(() => {
        const closeMenusFilters = (e) => {
            if (!filterRef?.current?.contains(e.target)) {
                setOpenFilter(null)
            }
        }

        document.body.addEventListener("mousedown", closeMenusFilters)

        return () => document.body.removeEventListener("mousedown", closeMenusFilters)
    }, [])

    // Efecto: Obtener opciones de filtros del backend (una sola vez)
    useEffect(() => {
        if (!userLogged) return

        const controller = new AbortController()

        const { signal } = controller

        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID
        }

        getContractsListFilters({ signal, params }).then(data => {
            setFiltersData(data)
        }).catch(err => console.error(err))

        return () => controller.abort()
    }, [userLogged])

    // Efecto: Cada cambio de filtro, paginación, orden manda a traer data filtrada/paginada del back
    useEffect(() => {
        if (!userLogged) return

        const controller = new AbortController()

        const { signal } = controller

        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID,
            searchValue: searchValueApplied,
            supplier: supplierFilterApplied,
            order,
            orderBy,
            page: currentPage,
            limit: ITEMS_PER_PAGE
        }

        getContractsList({ params, signal }).then(data => {
            setData(data?.contractsList ?? [])
            setTotal(data?.total ?? 0)
        }).catch(err => console.error(err))

        return () => controller.abort()
    }, [userLogged, searchValueApplied, supplierFilterApplied, order, orderBy, currentPage])


    // Handlers UI
    const handleOpenFilter = useCallback((e, filterName) => {
        filterRef.current = e.target
        setOpenFilter(prev => prev === filterName ? null : filterName)
    }, [])

    const toggleFilterValue = useCallback((setFilter) => (target) => {
        setFilter(prev => prev.includes(target.value)
            ? prev.filter(val => val !== target.value)
            : [...prev, target.value])
    }, [])

    const applyFilter = useCallback((filter) => {
        setCurrentPage(1)
        if (searchValueApplied || searchValue) {
            setSearchValue("")
            setSearchValueApplied("")
        }

        if (filter === "supplier") {
            setSupplierFilterApplied(supplierFilter)
        }
    }, [searchValue, searchValueApplied, supplierFilter])

    const clearFilter = useCallback((filter) => {
        switch (filter) {
            case "supplier":
                setSupplierFilter([])
                setSupplierFilterApplied([])
                break
            default:
                setSupplierFilter([])
                setSupplierFilterApplied([])
                break
        }

        setCurrentPage(1)
    }, [])

    // Sort
    const sortTable = useCallback((sortBy) => {
        setOrder(prev => prev === "ASC" ? "DSC" : "ASC")
        setOrderBy(sortBy)
    }, [])

    // Paginación
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE)
    const paginate = (pageNumber) => {
        if (pageNumber < 1 || pageNumber > totalPages) return
        setCurrentPage(pageNumber)
    }

    const handleSearchValue = useCallback((e) => {
        const { value } = e.target
        setSearchValue(value)
    }, [])

    const handleApplySearchValue = useCallback((e) => {
        e.preventDefault()

        if (supplierFilter?.length > 0 || supplierFilterApplied?.length > 0) {
            setSupplierFilter([])
            setSupplierFilterApplied([])
        }

        setSearchValueApplied(searchValue)
    }, [searchValue, supplierFilter, supplierFilterApplied])

    // FUNCTION TO CHANGE THE SPECIFIC CONTRACT STATUS FROM THE SPECIFIC SUPPLIER
    const confirmReviewContract = (supplierID, contractID) => {
        reviewContract(supplierID, contractID).then(() => {
            
            setData(prev => prev.filter(contract => contract.contractID !== contractID))
        }).catch(err => console.error(err))
    }

    // Estilos
    const mainStyle = {
        background: darkLightMode ? "#131E29" : "white",
        color: darkLightMode ? "white" : "inherit"
    }

    return (
        <section className={`contracts ${darkLightMode ? "darkMode" : null}`}>
            <div className="filters">
                {/* Filtro Supplier */}
                <div className={`contractsFilter ${openFilter === "supplier" ? "show" : null} ${supplierFilterApplied.length ? "active" : undefined}`} onClick={e => handleOpenFilter(e, "supplier")}>
                    <img src={person} alt="Prodensa Supplier" />
                    <span>Supplier</span>
                    <div className={`contractsFilterOptions ${openFilter === "supplier" ? "show" : null}`} onClick={e => !e.target.matches("button") && e.stopPropagation()}>
                        <span className="title">
                            <img className="d-lg-none imgMenu" src={person} alt="Prodensa Supplier" />
                            Supplier
                        </span>
                        <div className="optionsScroll">
                            {filtersData.suppliers?.map((supplier, index) => {
                                return (
                                    <div key={index} className="option">
                                        <input checked={supplierFilter.includes(supplier)} className="supplierCheck" value={supplier} type="checkbox" id={`supplier${index}`} onChange={e => toggleFilterValue(setSupplierFilter)(e.target)} />
                                        <label htmlFor={`supplier${index}`} >{supplier}</label>
                                    </div>
                                )
                            })}
                        </div>
                        <div className="actions">
                            <button onClick={() => clearFilter("supplier")}>Clear</button>
                            <button onClick={() => applyFilter("supplier")}>Apply</button>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleApplySearchValue}>
                    <div className={`searchContainer ${darkLightMode ? "darkMode" : null}`}>
                        <input id="generalSearch" value={searchValue} type="text" placeholder="Search by name..."
                            onChange={handleSearchValue} />
                        <button className="applySearchButton" type="submit"></button>
                    </div>
                </form>
            </div>

            <table className="tableContracts">
                <thead>
                    <tr>
                        <th style={mainStyle}>
                            <span>Name</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("companyName")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Status</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("status")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Client</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("entityName")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Contract Name</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("contractName")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Contract Status</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("contractStatus")} />
                        </th>
                        <th style={mainStyle}>
                            <span>Expiration</span>
                            <img className="sort" src={sortIcon} alt="Prodensa supplier" onClick={() => sortTable("contractExpire")} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {data?.map((supplier, index) => (
                        <tr key={index}>
                            <td>
                                <div className="infoContainer">
                                    <span onClick={() => navigate("/home/suppliers/supplierProfile/" + supplier.supplierID)}>
                                        {supplier.companyName}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span className={`${supplier.status === "Active" ? "success" : ""} ${supplier.status === "Hold payment" ? "failedAlone" : ""} ${supplier.status === "Ready for review" ? "success" : ""} ${supplier.status === "Pre-authorized" || supplier.status === "Review requested" ? "pending" : ""}`}>
                                        {supplier.status}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{supplier.entityName}</span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{supplier.contractName}</span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span className={`${supplier.contractStatus === "Active" ? "successAlone" : "failedAlone"}`}>
                                        {supplier.contractStatus}
                                    </span>
                                </div>
                            </td>
                            <td>
                                <div className="infoContainer">
                                    <span>{moment(supplier.contractExpire).format("DD MMMM YYYY")}</span>
                                    {supplier.contractStatus === "Expired" && <img className="reviewed" src={reviewed} alt="Prodensa Supplier" onClick={() => confirmReviewContract(supplier.supplierID, supplier.contractID)} />}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="d-lg-none contractList">
                {data?.map((supplier, index) => (
                    <div key={index} className="contractRow" onClick={() => navigate("/home/suppliers/supplierProfile/" + supplier.supplierID)}>
                        <h5 className="contractName">
                            {supplier.companyName}
                            {supplier.contractStatus === "Expired" && <img className="reviewed" src={reviewed} alt="Prodensa Supplier" onClick={(e) => { e.stopPropagation(); confirmReviewContract(supplier.supplierID, supplier.contractID) }} />}
                        </h5>
                        <div className="contractInformation">
                            <span className={`contractValue ${supplier.status === "Active" ? "success" : ""} ${supplier.status === "Hold payment" ? "failedAlone" : ""} ${supplier.status === "Ready for review" ? "success" : ""} ${supplier.status === "Pre-authorized" || supplier.status === "Review requested" ? "pending" : ""}`}>{supplier.status}</span>
                            <span className="contractTitle">Status</span>
                            <span className="contractValue">{supplier.entityName}</span>
                            <span className="contractTitle">Client</span>
                            <span className="contractValue">{supplier.contractName}</span>
                            <span className="contractTitle">Contract Name</span>
                            <span className={`contractValue ${supplier.contractStatus === "Active" ? "successAlone" : "failedAlone"}`}>{supplier.contractStatus}</span>
                            <span className="contractTitle">Contract Status</span>
                            <span className="contractValue">{moment(supplier.contractExpire).format("DD MMMM YYYY")}</span>
                            <span className="contractTitle">Expiration</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="addAndPagination">
                <PageSelector
                    currentPage={currentPage}
                    totalItems={total || 0}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onChange={paginate}
                />
            </div>
            <span className="tableCount">{total < ITEMS_PER_PAGE * currentPage ? total : ITEMS_PER_PAGE * currentPage} of {total}</span>
        </section>
    )
}
